/**
 * Utility functions for the Labour Barometer feature.
 * Supports capability-based rendering and deficit count calculation.
 *
 * @see docs/specifications/2026-03-06-kfs-barometr-zawodow.md
 */

import { BarometerDistrict } from "../../../../types/grants";

/** Program shape used for capability checks (accepts any program-like object) */
interface ProgramLike {
  capabilities?: string[];
  [key: string]: unknown;
}

/**
 * Checks whether a program has the LABOUR_BAROMETER capability.
 * Returns false for null/undefined programs or missing capabilities.
 *
 * @param program - Program object or null
 * @returns true if program has LABOUR_BAROMETER capability
 */
export function hasBarometerCapability(
  program: ProgramLike | null | undefined
): boolean {
  if (!program) return false;
  if (!program.capabilities) return false;
  return program.capabilities.includes("LABOUR_BAROMETER");
}

/**
 * Returns the total count of deficit professions across all districts.
 * Sums both largeShortageProfessions and shortageProfessions.
 *
 * @param districts - Array of barometer district data
 * @returns Total number of deficit professions
 */
export function getTotalDeficitCount(districts: BarometerDistrict[]): number {
  return districts.reduce(
    (total, district) =>
      total +
      district.largeShortageProfessions.length +
      district.shortageProfessions.length,
    0
  );
}

/**
 * Extracts TERYT district codes from a call item.
 * District codes come from project.subregions, with fallback to operator.district
 * (for KFS calls where projectId is often null).
 *
 * @param call - Call item with optional project and operator data
 * @returns Array of TERYT 4-digit district codes
 */
export function getDistrictCodes(
  call: {
    project?: { subregions?: string[] } | null;
    operator?: { district?: string; [key: string]: unknown } | null;
  } | null | undefined
): string[] {
  if (!call) return [];
  // Primary: project.subregions
  if (call.project?.subregions && call.project.subregions.length > 0) {
    return call.project.subregions;
  }
  // Fallback: operator.district (TERYT code) for KFS calls without project
  if (call.operator?.district) {
    return [call.operator.district];
  }
  return [];
}
